import React, { useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminModal from '../Admin/AdminModal'
import ShareRecipientPicker from './ShareRecipientPicker'

function formatNotificationStatus(note) {
  if (!note) return null
  const smtp = note.smtp
  const inbox = note.platformInbox
  const smtpLabel = smtp?.delivered
    ? 'Email sent'
    : smtp?.devFallback
      ? 'Email logged (SMTP not configured)'
      : smtp?.skipped
        ? 'Email skipped (notifications off)'
        : smtp?.error
          ? 'Email failed'
          : null
  const inboxLabel = inbox?.delivered
    ? 'Platform inbox'
    : inbox?.skipped
      ? 'Platform inbox after registration'
      : inbox?.error
        ? 'Platform inbox failed'
        : null
  return [smtpLabel, inboxLabel].filter(Boolean).join(' · ')
}

function parseExtraEmails(text) {
  return [...new Set(
    String(text || '')
      .split(/[\n,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  )]
}

function buildRecipients(selectedUsers, extraEmailsText) {
  const fromUsers = selectedUsers.map((u) => u.email).filter(Boolean)
  const manual = parseExtraEmails(extraEmailsText)
  return [...new Set([...fromUsers, ...manual])]
}

export default function SharePackageModal({ open, folder, onClose, onCreated }) {
  const [title, setTitle] = useState('')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [extraEmailsText, setExtraEmailsText] = useState('')
  const [validityDays, setValidityDays] = useState(30)
  const [allowDownload, setAllowDownload] = useState(true)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)

  const reset = () => {
    setTitle('')
    setSelectedUsers([])
    setExtraEmailsText('')
    setValidityDays(30)
    setAllowDownload(true)
    setResult(null)
  }

  const handleClose = () => {
    if (saving) return
    reset()
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!folder?.id) return
    const recipients = buildRecipients(selectedUsers, extraEmailsText)
    if (!recipients.length) {
      toast.error('Select at least one member or enter an email')
      return
    }
    setSaving(true)
    try {
      const res = await axios.post('/api/share-packages', {
        folderId: folder.id,
        title: title.trim() || folder.name,
        recipients,
        validityDays: Number(validityDays) || 30,
        allowDownload,
      })
      setResult(res.data)
      toast.success('Share created — notifications sent where possible')
      onCreated?.(res.data)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create share')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminModal
      open={open}
      title="Share Project Folder"
      subtitle={folder ? `Sharing: ${folder.name}` : ''}
      onClose={handleClose}
      wide
      footer={result ? (
        <button type="button" className="admin-btn primary" onClick={handleClose}>Done</button>
      ) : (
        <>
          <button type="button" className="admin-btn secondary" disabled={saving} onClick={handleClose}>Cancel</button>
          <button type="submit" form="share-package-form" className="admin-btn primary" disabled={saving}>
            {saving ? 'Sharing…' : 'Create Share'}
          </button>
        </>
      )}
    >
      {result ? (
        <div className="admin-share-result">
          <p className="admin-share-result-lead">
            Recipients are notified by <strong>platform email</strong> (Email → Platform Inbox) when already registered,
            and by <strong>real email</strong> to their work address when SMTP is configured.
            They can also open the folder under <strong>Documents → Shared with Me</strong> after login.
          </p>
          <p style={{ fontSize: 13, color: '#606266' }}>
            Valid until: {new Date(result.expiresAt).toLocaleString()}
          </p>
          <div className="admin-share-codes">
            {result.grants?.map((g) => {
              const note = result.notifications?.find((n) => n.grantId === g.grantId)
              return (
              <div key={g.grantId} className="admin-share-code-row">
                <div>
                  <strong>{g.email}</strong>
                  <div style={{ fontSize: 12, color: '#909399' }}>Download code (email-bound)</div>
                  {formatNotificationStatus(note) ? (
                    <div style={{ fontSize: 12, color: '#0f3d8a', marginTop: 4 }}>{formatNotificationStatus(note)}</div>
                  ) : null}
                </div>
                <code className="admin-share-code-value">{g.accessCode}</code>
              </div>
              )
            })}
          </div>
        </div>
      ) : (
        <form id="share-package-form" onSubmit={handleSubmit} className="admin-settings-grid-2">
          <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
            <span className="admin-field-label">Share title</span>
            <input
              className="admin-field-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={folder?.name || 'Project data room'}
            />
          </label>

          <div className="admin-field" style={{ gridColumn: '1 / -1' }}>
            <span className="admin-field-label">Platform members</span>
            <p className="admin-share-recipient-hint">
              Recent chat contacts appear first. Filter by GP/LP or fuzzy-search by name, company, or email.
            </p>
            <ShareRecipientPicker selected={selectedUsers} onChange={setSelectedUsers} />
          </div>

          <label className="admin-field" style={{ gridColumn: '1 / -1' }}>
            <span className="admin-field-label">Additional emails (optional)</span>
            <textarea
              className="admin-field-input"
              rows={3}
              value={extraEmailsText}
              onChange={(e) => setExtraEmailsText(e.target.value)}
              placeholder="For people not on the platform yet — one@company.com, two@company.com"
            />
          </label>

          <div className="admin-share-form-options">
            <label className="admin-share-validity-field">
              <span className="admin-field-label">Valid for (days)</span>
              <input
                type="number"
                min={1}
                max={365}
                className="admin-field-input"
                value={validityDays}
                onChange={(e) => setValidityDays(e.target.value)}
              />
            </label>
            <label className="admin-share-download-toggle">
              <input
                type="checkbox"
                checked={allowDownload}
                onChange={(e) => setAllowDownload(e.target.checked)}
              />
              <span>Allow download (requires code)</span>
            </label>
          </div>
        </form>
      )}
    </AdminModal>
  )
}
